import {AfterViewInit, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {HeaderService} from "../../../tandem-core/services/header.service";
import {PageModel, TableConfiguration} from "../../../tandem-core/models/table-configuration";
import {FinancialPosition} from "../../models/financial-position";
import {ActivatedRoute, Router} from "@angular/router";
import {FinancialPositionService} from "../../services/financial-position.service";
import {AuthService} from "../../../auth/services/auth.service";
import {CurrencyService} from "../../../tandem-core/services/currency.service";
import {DialogService, TandemDialogConfig} from "../../../tandem-core/services/dialog.service";
import {FinancialPositionChartMetadataService} from "../../services/financial-position-chart-metadata.service";
import {FinancialPositionChartMetadata} from "../../models/financial-position-chart-metadata";
import {ThemingService} from "../../../tandem-core/services/theming.service";
import {BehaviorSubject, combineLatest, startWith, Subject, switchMap, takeUntil} from "rxjs";
import { EChartsOption } from 'echarts';
import {ChartService} from "../../../tandem-core/services/chart.service";
import {TandemUser} from "../../../auth/models/tandem-user";
import {VideoPlayerService} from "../../../tandem-core/services/video-player.service";
import {VideoPlayerComponent} from "../../../tandem-core/components/video-player/video-player.component";
import {TandemDatePipe} from "../../../tandem-core/directives/tandem-date.pipe";
import {UserService} from "../../../auth/user.service";

@Component({
  selector: 'tandem-financial-position-list',
  templateUrl: './financial-position-list.component.html',
  styleUrls: ['./financial-position-list.component.css']
})
export class FinancialPositionListComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() reviewMode: boolean = false;

  findStatementForm: FormGroup = new FormGroup<any>({});
  isStatementVisible: FormControl = new FormControl({value: true, disabled: false});

  tableConfig: TableConfiguration<FinancialPosition> = {
    title: undefined,
    noDataMessage: 'No Financial Positions found.',
    onRowClick: t => {this.router.navigate([`financial-positions/${t.id}`])},
    editRowFunction: t => {this.router.navigate([`financial-positions/${t.id}/edit`])},
    deleteRowFunction:  t => {this.onDelete(t)},
    columns: [
      {
        title: 'As Of',
        sortable: true,
        itemProperty: "positionDate",
        columnType: "text",
        displayMask: t => this.dateService.transform(t.positionDate.toDate()) || '',
        sortedByDefault: true
      },
      {
        title: 'Net Worth',
        sortable: true,
        itemProperty: "netWorth",
        columnType: "value",
        displayMask: t => this.currencyService.formatNumber(t.netWorth)
      },
      {
        title: 'Last Modified',
        sortable: true,
        itemProperty: "dateModified",
        displayMask: t => {return this.dateService.transform(t.dateModified.toDate()) || ''},
        columnType: "text"
      },
    ],
    paginated: true,
    sortColumn: 'positionDate',
    sortDirection: 'desc',
    pageSize: 5
    // collapsible: true,
    // collapsed: false
    // pageSizeOptions: [5, 10, 20]
  };
  data: FinancialPosition[] = [];

  tablePageModel$: BehaviorSubject<PageModel<FinancialPosition> | undefined> = new BehaviorSubject<PageModel<FinancialPosition> | undefined>({sortOrder: "desc", sortProperty: "positionDate"});

  reviewedUserId?: string;

  chartOptions: EChartsOption = {};

  user: TandemUser | null = null;
  private destroy$ = new Subject<void>();
  usingSampleData = false;

  @ViewChild('customContent', {static: false})
  public customContent?: TemplateRef<any>;

  constructor(private auth: AuthService,
              private fb: FormBuilder,
              private financialPositionService: FinancialPositionService,
              private metadataService: FinancialPositionChartMetadataService,
              private headerService: HeaderService,
              private router: Router,
              private route: ActivatedRoute,
              private dateService: TandemDatePipe,
              private currencyService: CurrencyService,
              private themingService: ThemingService,
              private dialogService: DialogService,
              private chartService: ChartService,
              private userService: UserService,
              private videoService: VideoPlayerService) {
  }

  ngAfterViewInit(): void {
    if (this.customContent) {
      this.headerService.setTemplateRef(this.customContent);
    }
    }

  ngOnInit(): void {
    this.findStatementForm = this.fb.group({
      startDate: null,
      endDate: null,
    });

    if (!this.reviewMode) {
      this.headerService.setConfig({
        title: 'Financial Positions',
        subtitle: 'See where you stand with your finances at a given point in time',
        breadcrumbs: [{label: 'Financial Positions'}],
        optionalTemplate: this.customContent
      });
    } else {
      this.tableConfig.onRowClick = t => {this.router.navigate([`${this.reviewMode ? 'coaching/user-management/' + this.reviewedUserId + '/' : ''}financial-positions/${t.id}`])}
      this.tableConfig.editRowFunction = undefined;
      this.tableConfig.deleteRowFunction = undefined;
      this.reviewedUserId = this.route.snapshot.paramMap.get('userId') || '';
    }
    combineLatest([
      this.auth.$user,
      this.findStatementForm.valueChanges.pipe(startWith(this.findStatementForm.value)),
      this.tablePageModel$.pipe(startWith({sortProperty: 'positionDate', sortOrder: 'desc'}))
    ]).pipe(
      takeUntil(this.destroy$),
      switchMap(([user, formValues, pageModel]) => {
        this.user = user;
        if (user) {

          if (!this.reviewMode) {
            this.headerService.setTitle(this.getTrackingToolName('plural'));
            this.headerService.setSubtitle(this.getTrackingToolDescription());
            this.headerService.setBreadcrumbs([{label: this.getTrackingToolName('plural')}]);
          }

          this.tableConfig.noDataMessage = `No ${this.getTrackingToolName('plural')} found.`;

          this.metadataService.listByUserId(this.reviewedUserId ? this.reviewedUserId : user.uid).subscribe(chartData => {
            this.chartOptions = {};
            if (chartData && chartData[0]?.charts?.length > 1) {
              this.usingSampleData = false;
              this.renderChart(chartData[0])
            } else {
              this.usingSampleData = true;
              this.renderChart(this.metadataService.generateSampleChartData(user.uid));
            }
          });
          const startDate = formValues.startDate ? new Date(formValues.startDate) : undefined;
          const endDate = formValues.endDate ? new Date(formValues.endDate) : undefined;
          return this.financialPositionService.listTrackingToolsByDateRange(this.reviewedUserId ? this.reviewedUserId : user.uid, 'positionDate', startDate, endDate, pageModel as PageModel<FinancialPosition>);
        }
        return [];
      })
    ).subscribe(positions => {
      this.data = [...positions];
      if (this.user?.promptFPVideo) {
        this.showVideo();
        this.user.promptFPVideo = false;
        this.userService.update(this.user)
      }
    });
    this.themingService.selectedTheme.subscribe(newTheme => {
      // TODO change primary color of chart
    });
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDelete(position: FinancialPosition) {
    let modalConfig: TandemDialogConfig = {
      title: `Delete ${this.getTrackingToolName('single')}`,
      type: 'confirm',
      content: `Are you sure you want to delete this ${this.getTrackingToolName('single')}? This action can't be undone.`,
      actions: [
        {
          text: 'Cancel',
          role: 'cancel',
          callback: () => {
            this.dialogService.closeModal2();
          }
        },
        {
          text: `Delete`,
          role: 'confirm',
          callback: () => {
            this.dialogService.closeModal2();
            this.financialPositionService.delete(position.id!).then(res => this.dialogService.showAlert({
              title: `Successfully Deleted ${this.getTrackingToolName('single')}`,
              position: 'bottom',
              content: `You successfully deleted '${position.title}'`,
              dismissTime: 5000
            }));
          }
        }
      ]
    }
    this.dialogService.openModal2(modalConfig);
  }

  onCreateNew(mostRecent: boolean = false) {
    this.router.navigate([`financial-positions/${mostRecent ? 'create/most-recent' : 'create'}`]);
  }

  onPageUpdate(pageModel: PageModel<FinancialPosition>) {
    this.tablePageModel$.next(pageModel);
  }

  private renderChart(chartData: FinancialPositionChartMetadata) {
    chartData.charts = chartData.charts.sort((a, b) => {
      if (a.positionDate && b.positionDate) {
        // let dateA = a.positionDate.toDate().getTime();
        // let dateB = b.positionDate.toDate().getTime();
        //
        // return dateA - dateB;
      } else {
        return 0;
      }
      return 0
    });
    let labels: string[] = chartData.charts
      .map(chart => chart.positionDate.toDate())
      .map(date => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`);
    let netWorthData: number[] = chartData.charts.map(d => d.netWorth);
    let totalAssetsData: number[] = chartData.charts.map(d => d.totalAssets);
    let totalLiabilitiesData: number[] = chartData.charts.map(d => d.totalLiabilities);

    this.chartOptions = this.chartService.generateLineChart('Date', 'Value', labels, [
      {name: 'Net Worth', data: netWorthData, color: `${this.themingService.getPrimaryRGB()}`},
      {name: 'Total Assets', data: totalAssetsData, color: `#4CAF50`, disableByDefault: true},
      {name: 'Total Liabilities', data: totalLiabilitiesData, color: `#F44336`, disableByDefault: true},
    ]);
  }

  getTrackingToolName(variant: 'single' | 'plural') {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.fpName ? this.user.customTTMetadata.fpName : 'Financial Position';
    } else {
      return this.user?.customTTMetadata?.fpNamePlural ? `${this.user.customTTMetadata.fpNamePlural}` : 'Financial Positions';
    }
  }

  getTrackingToolDescription() {
    return !!this.user?.customTTMetadata?.fpDescription ? this.user.customTTMetadata.fpDescription : 'A summary of what you own, your assets, and what you owe, your liabilities at a given point in time'
  }

  showVideo() {
    this.dialogService.openModal(VideoPlayerComponent, {videoType: 'financialPosition'}, {}, 'sm:w-[75vw]');
  }

  resetForm() {
    this.findStatementForm.reset();
  }
}
